import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import CheckIcon from "@mui/icons-material/Check";
import ActualOrderTab from "./ActualOrderTab";
import BondCharacteristicTab from "./BondCharacteristicTab";
import BondLadderTab from "./BondLadderTab";
import ListRfqTab from "./ListRfqTab";
import ListCancelButton from "./ListCancelButton";
import ListNamePrompt from "./ListNamePrompt";
import ListNameSaveButton from "./ListNameSaveButton";
import { LIST_RFQ_TAB_INDEX, useListRfqStore } from "../../Store/listRfqStore";
import { useListRfqPrefill } from "./useListRfqPrefill";
import { isListPrefillCandidate } from "./listRfqPrefill";
import { hasDuplicateCusips, meaningfulRows } from "./listRfqPayload";
import { tabStyle } from "./styles";
import { useOrderEntry } from "./useOrderEntry";
import { PROTOCOL_TYPES, USER_ROLES } from "../../Constants/AppConstants";
import {
  isDealerSelectionBlocked,
  isTradingDeskSelected,
  TRADING_DESK_SUBMIT_WARNING,
} from "./optionsHelper";
import { isStagingOrder, STAGING_ROUTE_NOTE } from "../../Constants/stagingBlotter";
import { tokens, withAlpha } from "../../Common/designTokens"; // added token import

const TabPanel = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    sx={{
      height: "100%",
      display: value === index ? "flex" : "none",
      flexDirection: "column",
      overflow: "hidden",
    }}
    {...other}
  >
    {value === index && children}
  </Box>
);

const OrderEntryCore = ({
  readOnly = false,
  onClose,
  source = "navbar",
  initialActiveTab = 0,
  tabName = "empty",
  cusip = "",
  side = "",
  qty = "",
  onOrderAdded,
  initialData,
  showTabs = true,
  showFooter = true,
  onSubmitSuccess,
  onClearAll,
  footerButtonStyle,
  containerStyle,
  qtyPriceEditMode = false,
  secondarySource,
}) => {
  const [activeTab, setActiveTab] = useState(
    source === "orderBlotter" && !readOnly && !initialData?.editRfqMode &&
      isListPrefillCandidate(initialData)
      ? 0
      : tabName === "ladder"
      ? 2
      : tabName === "characteristic"
      ? 1
      : tabName === "list"
      ? LIST_RFQ_TAB_INDEX
      : source === "orderBlotter" && !readOnly && !!initialData?.orderId &&
        initialData?.protocol === PROTOCOL_TYPES.MARKETAXESS
      ? 0
      : initialActiveTab
  );

  // NEW: Get CUSIP error from store for allocate account validation
  const cusipError = useActualOrderStore((state) => state.cusipError);

  const {
    // State
    isCusipValid,
    setIsCusipValid,
    bondDetailsMain,
    setBondsDetailsMain,
    setShowBondsDetails,
    showBondsDetails,
    showConfirmation,
    accountIdentifiers,
    loading,
    hasValidationErrors,
    setHasValidationErrors,
    error,
    setError,

    // Store data
    actualOrderData,
    bondCharacteristicData,
    bondLadderData,

    // Field errors
    orderFieldErrors,
    bondCharacteristicErrors,
    bondLadderErrors,

    // Actions
    handleSubmit,
    handleClearAll,
    clearDataInAllTabs,

    // Modal error helpers
    clearModalError,

    // Token and role
    token,
    tokenRole,
  } = useOrderEntry({
    source,
    secondarySource,
    initialData,
    cusip,
    side,
    qty,
    onOrderAdded,
    onSubmitSuccess,
    onClose,
  });
  const warningMessage = useActualOrderStore((state) => state.warningMessage);

  // Register clear callback with parent
  React.useEffect(() => {
    if (onClearAll) {
      onClearAll(() => clearDataInAllTabs);
    }
  }, [onClearAll, clearDataInAllTabs]);

  const isEditRfqMode = !!actualOrderData?.editRfqMode;

  // If edit RFQ, force bond order tab
  React.useEffect(() => {
    if (isEditRfqMode && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [isEditRfqMode, activeTab]);

  // NEW: If allocate account, force bond order tab
  React.useEffect(() => {
    if (source === "allocateAccount" && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [source, activeTab]);

  const handleTabChange = (event, newValue) => {
    if (isEditRfqMode) return; // prevent switching in edit RFQ mode

    // The footer error belongs to the tab that produced it, not to the modal. It is one piece
    // of state shared by every tab (useOrderEntry.error), so leaving it up after a switch
    // showed the trader a complaint about a form they were no longer looking at - "A list RFQ
    // needs at least one bond." sitting over the Bond Order ticket, or a failed single-bond
    // submit sitting over the List Order grid.
    //
    // Cleared rather than remembered per tab: the message is about an attempt that is over, and
    // every submit path sets it again from scratch. The red field borders are NOT cleared -
    // those live on each tab's own store, travel with it, and are still correct when the trader
    // comes back to fix them.
    //
    // clearModalError() rather than setError(null): it also resets lastErrorField, which
    // setError alone would leave pointing at a field on the tab just left - and a later
    // clearModalError("thatField") would then clear an unrelated tab's message.
    if (newValue !== activeTab) {
      clearModalError();
    }

    setActiveTab(newValue);
  };

  // Dealer options as offered by the Dealer dropdown, reported up from ActualOrderTab.
  const [dealerOptions, setDealerOptions] = useState({ options: [], loaded: false });
  const handleVenueOptionsChange = useCallback((next) => {
    setDealerOptions((prev) =>
      prev.loaded === next.loaded && prev.options === next.options ? prev : next
    );
  }, []);

  // Sending to market prefills the dealer from the staged order / the bond's broker-dealer,
  // and those names are often ones we do not trade with. The dropdown cannot render such a
  // value, so the Dealer field looks empty while the order still carries it — keep the send
  // button off instead of letting an unpickable dealer reach the market.
  const dealerBlocked = useMemo(() => {
    if (source !== "sendToMarket") return false;
    return isDealerSelectionBlocked({
      venue: actualOrderData?.venue,
      options: dealerOptions.options,
      optionsLoaded: dealerOptions.loaded,
      protocol: actualOrderData?.protocol,
      strategyType: actualOrderData?.strategyType,
      mktxDealerListMode: actualOrderData?.mktxDealerListMode,
    });
  }, [
    source,
    dealerOptions,
    actualOrderData,
    actualOrderData?.venue,
    actualOrderData?.protocol,
    actualOrderData?.strategyType,
    actualOrderData?.mktxDealerListMode,
  ]);

  // Trading Desk is offered in the send-to-market dealer list rather than hidden, because
  // a silently shortened list reads as a bug. Selecting it is allowed but cannot be
  // submitted — it would leave the order staged — so say that plainly instead of leaving
  // the trader with a disabled button and no reason.
  const dealerWarning = useMemo(() => {
    if (source !== "sendToMarket") return null;
    return isTradingDeskSelected(actualOrderData?.venue)
      ? TRADING_DESK_SUBMIT_WARNING
      : null;
  }, [source, actualOrderData?.venue]);

  // Informational, not a warning: say so whenever this draft is bound for the Staging
  // Blotter, so a Dealer field showing a real dealer is not misread as "this is where the
  // order is going".
  //
  // Keyed on the staging flag rather than on `source`, because more than one entry point
  // raises a staged order: "Add Order" on a ladder-linked characteristic
  // (source "orderViaCharacteristic", Dealer editable) and clicking a ladder-linked
  // inventory row (source "inventory", Dealer locked to that row's broker/dealer). An
  // earlier version tested the source and so missed the second one entirely. Using the same
  // predicate that decides routing means the note cannot disagree with where the order
  // actually goes.
  //
  // Excluded for send-to-market: that flow is taking an order OUT of staging, and its
  // Trading-Desk case is already covered by dealerWarning above.
  const stagingRouteNote = useMemo(() => {
    if (source === "sendToMarket") return null;
    return isStagingOrder(actualOrderData) ? STAGING_ROUTE_NOTE : null;
  }, [source, actualOrderData]);

  /**
   * WHICH send is in flight - null, "single" or "list".
   *
   * In plain terms: so the spinner appears on the button the trader actually pressed.
   *
   * `loading` from useOrderEntry says only THAT something is in flight, not what. With two send
   * buttons on screen that is not enough - reading it directly spun both at once, which told the
   * trader nothing about what they had just done. This records the action, so each button can
   * ask "is it me?".
   *
   * Disabling still keys off `loading`, deliberately: while anything is sending, neither button
   * is clickable. Only the spinner is scoped.
   */
  const [submittingAs, setSubmittingAs] = useState(null);

  const onSubmit = async () => {
    // dealerBlocked describes the Bond Order tab's dealer selection - see isListRfqTab above.
    if (dealerBlocked && !isListRfqTab) return;
    setSubmittingAs("single");
    try {
      await handleSubmit(activeTab);
    } finally {
      // Cleared here rather than on success, so a rejected submit returns the button to its
      // label instead of leaving it spinning over a form the trader still has to fix.
      setSubmittingAs(null);
    }
  };

  /**
   * Is "Send List Order" offered alongside "Send Order"?
   *
   * In plain terms: only when the trader is releasing a whole staged bond ladder, and only when
   * that ladder is going to Trumid or MarketAxess, both of which support list inquiries.
   *
   * Both conditions are needed. secondarySource says this ticket is the ladder-level release
   * (StagingBlotterTable sets it for a ladder GROUP row, never for a single order), and that is
   * what makes "a list" meaningful here: the backend already finds every staged bond of the
   * ladder from bondLadderId. Any other entry point has one bond and nothing to group.
   *
   * No minimum of two bonds: a ladder with one bond left still goes as a list if the trader asks
   * for it, and the backend accepts a single-bond list (createOrderList's floor is one). The
   * frontend cannot count them anyway - this ticket carries no cusips, only the ladder id.
   */
  const canSendLadderAsList =
    secondarySource === "ladder-level-send-to-market" &&
    [PROTOCOL_TYPES.TRUMID_RFQ, PROTOCOL_TYPES.MARKETAXESS].includes(actualOrderData?.protocol);

  /**
   * Sends the ladder as ONE venue list inquiry instead of one per bond.
   *
   * Identical to onSubmit in every other respect - same tab, same validation, same guard - so
   * the two buttons cannot diverge on what they consider sendable. The only difference is the
   * flag, which useOrderEntry puts on the payload and the backend reads in
   * handleBondLadderCommand.
   */
  // Send List Order first asks for the list's name (ListNamePrompt); onSubmitAsList runs on its Send.
  const [listNamePromptOpen, setListNamePromptOpen] = useState(false);
  const [savingListName, setSavingListName] = useState(false);
  const savedListName = useListRfqStore((state) => state.listRfqData.savedListName);
  const ownsList = useListRfqStore((state) => state.listRfqData.canEditListName === true);
  const canEditListName = !!(readOnly && initialData?.viewMode === "list" && initialData?.listId && ownsList);

  const onSubmitAsList = async (listName) => {
    setListNamePromptOpen(false);
    if (dealerBlocked && !isListRfqTab) return;
    setSubmittingAs("list");
    try {
      await handleSubmit(activeTab, { sendAsList: true, listName });
    } finally {
      setSubmittingAs(null);
    }
  };

  const onClearAllClick = () => {
    // For edit RFQ mode: cancel means clear data and close popup
    if (isEditRfqMode ||source === "orderViaCharacteristic" ) {
    // NEW: Handle orderViaCharacteristic source
      handleClearAll();
      if (onClose) onClose();
      return;
    }

    
    // "Cancel" semantics for sendToMarket: clear data and close popup
    if (source === "sendToMarket") {
      handleClearAll();
      if (onClose) onClose();
      return;
    }

    // Preserve existing behavior for other sources
    if (readOnly) {
      // If readonly, clear data and close popup
      handleClearAll();
      if (onClose) onClose();
    } else {
      // If not readonly, just clear data
      handleClearAll();
    }
  };

  // Enforce qty-edit only for TRADER role
  const effectiveQtyPriceEditMode = !!qtyPriceEditMode;

  // NEW: detect Trumid RFQ or MarketAxess with no settlement date to prevent submit
  // Only enforce settlement date for Trumid RFQ (do not block for MarketAxess)
  const isMissingSettlement = useMemo(() =>
    (actualOrderData?.protocol === PROTOCOL_TYPES.TRUMID_RFQ) &&
    !actualOrderData?.settlementDate,
    [actualOrderData?.protocol, actualOrderData?.settlementDate]
  );
  const enforceSettlementCheck = source !== "allocateAccount";

  // isMissingSettlement and dealerBlocked are both read off actualOrderData, which belongs
  // to the Bond Order tab. On the List RFQ tab they would describe a different ticket
  // entirely - a half-filled single order left on tab 0 would disable the list's Send
  // button for no visible reason. The list has its own validation (validateListRfq) and
  // fetches its settlement date at submit time, exactly as the single-bond path does.
  const isListRfqTab = activeTab === LIST_RFQ_TAB_INDEX;

  // The same bond twice in one list cannot be sent, so Send is off while it is on screen -
  // refusing at submit would be telling the trader something the screen already knows. The
  // offending cells are red-bordered at the same moment (ListRfqBondRow), and the identical
  // rule is repeated in validateListRfq because POST /order/list is reachable without this UI.
  //
  // Selecting a boolean rather than the rows themselves keeps this out of the render path of
  // every keystroke: the value only changes when a clash appears or clears.
  const listHasDuplicateCusips = useListRfqStore((state) => hasDuplicateCusips(state.rows));

  // Nothing to send until at least one bond has been started, so Send stays off rather than
  // the screen carrying a note saying a bond is needed - the empty grid says that already.
  // One is the floor the backend uses too (createOrderList: "a list RFQ needs at least one
  // bond"); a blank trailing row does not count, which is what meaningfulRows decides.
  const listHasNoBonds = useListRfqStore((state) => meaningfulRows(state.rows).length === 0);

  // Re-Initiate on a Trumid or MarketAxess list bond can restore its whole list.
  const listPrefillLoading = useListRfqPrefill({
    token,
    order: initialData,
    // Reinitiate restores the list in the background while Bond Order stays open.
    active: isListRfqTab || (source === "orderBlotter" && isListPrefillCandidate(initialData)),
    enabled:
      !readOnly &&
      !isEditRfqMode &&
      source !== "allocateAccount" &&
      source !== "maInventoryAddOrder",
  });
  const shouldDisableForSettlement =
    !isListRfqTab && enforceSettlementCheck && isMissingSettlement;

  // NEW: check if allocate button should be disabled
  const isAllocateDisabled = useMemo(() => {
    if (source === "allocateAccount") {
      return loading;
    }
    return false;
  }, [source, loading]);

  const isCancelButton = isEditRfqMode || source === "sendToMarket" || source === "allocateAccount" || source === "maInventoryAddOrder" || readOnly;
  const handleCancelClick = () => {
    handleClearAll();
    if (onClose) onClose();
  };
  const handleClearAllOnly = () => {
    handleClearAll();
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      overflow: "hidden",
      ...containerStyle,
    }}>
      {!showConfirmation ? (
        <>
          {showTabs && !readOnly && source !== "sendToMarket" && source !== "buyOrderEntry" && source !== "allocateAccount" && !isEditRfqMode && source !== "orderViaCharacteristic" && source !== "maInventoryAddOrder" && (
            <Box sx={{ flexShrink: 0 }}>
              <Tabs
                sx={{ pl: "15px", pr: "20px" }}
                value={activeTab}
                onChange={handleTabChange}
                aria-label="order entry tabs"
              >
                <Tab label="Bond Order" sx={tabStyle} />
                <Tab label="Bond Characteristic" sx={tabStyle} />
                <Tab label="Bond Ladder" sx={tabStyle} />
                {/* List RFQ is appended, so the three existing tabs keep their indexes and
                    every flow that forces a tab by number (edit RFQ, allocate account)
                    still lands where it did. */}
                <Tab label="List Order" sx={tabStyle} />
              </Tabs>
            </Box>
          )}

          <Box sx={{ 
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}>
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
                <ActualOrderTab
                  inventoryId={initialData?.bondInventoryId}
                  readOnly={source === "sendToMarket" ? "partial" : source === "allocateAccount" ? "allocate" : readOnly} // NEW
                  qtyPriceEditMode={effectiveQtyPriceEditMode}
                  accountIdentifiers={accountIdentifiers}
                  token={token}
                  cusip={cusip}
                  side={side}
                  qty={qty}
                  errors={orderFieldErrors}
                  onVenueOptionsChange={handleVenueOptionsChange}
                  source={source}
                  secondarySource={secondarySource}
                  onCusipValidationChange={setIsCusipValid}
                  clearModalError={clearModalError}
                  setBondsDetailsMain={setBondsDetailsMain}
                  showBondsDetails={showBondsDetails}
                  setShowBondsDetails={setShowBondsDetails}
                />
              </Box>
            </TabPanel>
            {!isEditRfqMode && source !== "allocateAccount" && source !== "maInventoryAddOrder" && (
              <TabPanel value={activeTab} index={1}>
                <BondCharacteristicTab
                  readOnly={readOnly}
                  accountIdentifiers={accountIdentifiers}
                  errors={bondCharacteristicErrors}
                  onValidationChange={setHasValidationErrors}
                  secondarySource={secondarySource}
                />
              </TabPanel>
            )}
            {!isEditRfqMode && source !== "allocateAccount" && source !== "maInventoryAddOrder" && (
              <TabPanel value={activeTab} index={2}>
                <BondLadderTab
                  readOnly={readOnly}
                  accountIdentifiers={accountIdentifiers}
                  errors={bondLadderErrors}
                  secondarySource={secondarySource}
                />
              </TabPanel>
            )}
            {/* Same visibility rule as the two tabs above: a list is a fresh enquiry, so it
                is not offered when the modal is editing or allocating an existing order. */}
            {!isEditRfqMode && source !== "allocateAccount" && source !== "maInventoryAddOrder" && (
              <TabPanel value={activeTab} index={LIST_RFQ_TAB_INDEX}>
                <ListRfqTab
                  token={token}
                  accountIdentifiers={accountIdentifiers}
                  readOnly={!!readOnly}
                  prefillLoading={listPrefillLoading}
                  allowListNameEdit={canEditListName && !savingListName}
                />
              </TabPanel>
            )}
          </Box>

          {showFooter && (
            <Box sx={{ 
              flexShrink: 0,
              display: "flex", 
              flexDirection: "column",
              p: 2, 
              borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
              backgroundColor: tokens.colors.defaultGridRowColor,
            }}>
              {error && (
                <Box sx={{ 
                  mb: 2,
                  p: 2,
                  backgroundColor: withAlpha(tokens.palette.danger, 0.133), // use helper instead of string concat
                  border: `1px solid ${tokens.palette.danger}`,
                  borderRadius: "8px",
                }}>
                  <Typography sx={{ 
                    color: tokens.palette.danger,
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {error}
                  </Typography>
                </Box>
              )}
              {/* Both notes below are worked out from actualOrderData - the Bond Order tab's
                  state - so on the List Order tab they describe a ticket that is not on screen:
                  its dealer, its staging destination. The list has its own dealers per bond and
                  is never staged, so neither can ever be true of it.

                  Gated on the LIST tab rather than on "tab 0", deliberately. The modal can open
                  straight onto the Bond Ladder tab (tabName === "ladder"), and ladder-level
                  send-to-market reads its dealer from this same actualOrderData - so gating to
                  tab 0 would have silently removed the Trading Desk warning from the flow that
                  needs it most. Same reasoning, and same test, as dealerBlocked above. */}
              {!error && !isListRfqTab && stagingRouteNote && (
                <Box sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: tokens.colors.bodyHoverBackgroundColor,
                  border: `1px solid ${tokens.colors.bodyBorderColor}`,
                  borderRadius: "8px",
                }}>
                  <Typography sx={{
                    color: tokens.colors.fontColour,
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {stagingRouteNote}
                  </Typography>
                </Box>
              )}
              {!error && !isListRfqTab && (dealerWarning || warningMessage) && (
                <Box sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: withAlpha(tokens.colors.amberColor, 0.16),
                  border: `1px solid ${tokens.colors.amberColor}`,
                  borderRadius: "8px",
                }}>
                  <Typography sx={{
                    color: tokens.colors.amberColor,
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {dealerWarning || warningMessage}
                  </Typography>
                </Box>
              )}
              <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}>
                {/* Saved-list actions: Cancel List on the left, Cancel then Save on the right. */}
                {readOnly && isListRfqTab && initialData?.viewMode === "list" && (
                  <Box sx={{ mr: "auto" }}>
                    <ListCancelButton
                      disabled={savingListName}
                      order={{ ...initialData, listName: savedListName ?? initialData.listName }}
                      buttonSx={{
                        px: 4,
                        py: 1,
                        width: "155px",
                        height: "46px",
                        borderRadius: "8px",
                        ...footerButtonStyle,
                      }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  onClick={isCancelButton ? handleCancelClick : handleClearAllOnly}
                  disabled={savingListName}
                  sx={{
                    textTransform: "none",
                    fontWeight: "700",
                    px: 4,
                    py: 1,
                    width: "150px",
                    height: "46px",
                    borderRadius: "8px",
                    ...footerButtonStyle,
                  }}
                >
                  {isCancelButton ? "Cancel" : "Clear All"}
                </Button>
                {canEditListName && isListRfqTab && (
                  <ListNameSaveButton key={initialData.listId} token={token} listId={initialData.listId}
                    disabled={savingListName || listPrefillLoading}
                    onError={setError}
                    onSavingChange={setSavingListName}
                    buttonSx={{ textTransform: "none", fontWeight: "700", px: 4, py: 1,
                      height: "46px", borderRadius: "8px", ...footerButtonStyle }} />
                )}
                {/* show Update button when in qty-edit readOnly mode */}
                {readOnly && effectiveQtyPriceEditMode && (
                  <Button
                    variant="contained"
                    onClick={async () => { await handleSubmit(activeTab, { isQtyPriceUpdate: true }); }}
                    disabled={loading || shouldDisableForSettlement}
                    sx={{
                      textTransform: "none",
                      fontWeight: "700",
                      px: 4,
                      py: 1,
                      width: "150px",
                      height: "46px",
                      borderRadius: "8px",
                      backgroundColor: tokens.palette.primary,
                      '&:hover': { backgroundColor: tokens.palette.primaryA80 },
                      // Explicit visual for disabled state: primary color with reduced opacity
                      '&.Mui-disabled': {
                        backgroundColor: withAlpha(tokens.palette.primary, 0.45),
                        color: tokens.colors.white,
                        opacity: 1, // keep color vivid but semi-transparent background
                        cursor: 'not-allowed',
                      },
                      ...footerButtonStyle,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Update"
                    )}
                  </Button>
                )}
                {!readOnly && (
                  <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={
                      loading ||
                      shouldDisableForSettlement ||
                      isAllocateDisabled ||
                      (dealerBlocked && !isListRfqTab) ||
                      (isListRfqTab && (listHasDuplicateCusips || listHasNoBonds))
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: "700",
                      px: 4,
                      py: 1,
                      width: "155px",
                      height: "46px",
                      borderRadius: "8px",
                      backgroundColor: tokens.palette.primary,
                      '&:hover': { backgroundColor: tokens.palette.primaryA80 },
                      '&.Mui-disabled': {
                        backgroundColor: withAlpha(tokens.palette.primary, 0.45),
                        color: tokens.colors.white,
                        opacity: 1,
                        cursor: 'not-allowed',
                      },
                      ...footerButtonStyle,
                    }}
                  >
                    {/* Only when THIS button was the one pressed - see submittingAs. Still
                        disabled while any send is in flight, just not spinning. */}
                    {submittingAs === "single" ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : isEditRfqMode ? (
                      "Modify"
                    ) : source === "sendToMarket" ? (
                      "Send Order"
                    ) : source === "allocateAccount" ? (
                      "Allocate"
                    ) : (
                      "Submit"
                    )}
                  </Button>
                )}
                {/* Sits AFTER Send Order, so it is the last and right-most control: Send
                    Order stays the primary action and this reads as the deliberate
                    alternative. Both remain available - the trader chooses whether the ladder
                    goes out as one inquiry or as one per bond.

                    Black outline and black text rather than the primary blue, so it does not
                    compete with Send Order for the eye. Wide enough, and nowrap, so the label
                    stays on one line - "Send List Order" at 700 weight needs more room than
                    the 155px Send Order button.

                    Same disabled conditions as Send Order, so it can never be clickable at a
                    moment when Send Order is not. */}
                {!readOnly && canSendLadderAsList && (
                  <Button
                    variant="outlined"
                    onClick={() => setListNamePromptOpen(true)}
                    disabled={
                      loading ||
                      shouldDisableForSettlement ||
                      isAllocateDisabled ||
                      (dealerBlocked && !isListRfqTab)
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: "700",
                      px: 2,
                      py: 1,
                      width: "190px",
                      height: "46px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                      borderColor: tokens.colors.fontColour,
                      color: tokens.colors.fontColour,
                      '&:hover': {
                        borderColor: tokens.colors.fontColour,
                        backgroundColor: withAlpha(tokens.colors.fontColour, 0.08),
                      },
                      '&.Mui-disabled': {
                        borderColor: withAlpha(tokens.colors.fontColour, 0.45),
                        color: withAlpha(tokens.colors.fontColour, 0.45),
                        cursor: 'not-allowed',
                      },
                      ...footerButtonStyle,
                    }}
                  >
                    {submittingAs === "list" ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send List Order"
                    )}
                  </Button>
                )}
                {canSendLadderAsList && (
                  <ListNamePrompt
                    open={listNamePromptOpen}
                    protocol={actualOrderData?.protocol}
                    onCancel={() => setListNamePromptOpen(false)}
                    onSend={onSubmitAsList}
                  />
                )}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          backgroundColor: tokens.colors.bodyBackgroundColor,
          borderRadius: "8px",
        }}>
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            width: "100%",
            py: 6,
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              border: `2px solid ${tokens.palette.primary}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}>
              <CheckIcon sx={{ fontSize: 32, color: tokens.palette.primary }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: "500" }}>
              {source === "sendToMarket"
                ? "Order Successfully Updated"
                : "Order Successfully Placed"}
            </Typography>
          </Box>
          <Box sx={{ width: "100%", height: 1, backgroundColor: "#e0e0e0" }} />
          <Box sx={{
            width: "100%",
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                backgroundColor: tokens.palette.primary,
                color: tokens.colors.white,
                textTransform: "none",
                borderRadius: "8px",
                width: 150,
                py: 1,
                "&:hover": { backgroundColor: tokens.palette.primaryA80 },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrderEntryCore;
